import React, { useEffect } from "react";
import { useGlobal } from "reactn";
import { useQuery } from "@apollo/client";
import { Switch, Redirect, Route, useLocation } from "react-router-dom";
import loadable from "@loadable/component";
import Footer from "@ugitgud/legos/ui/footer/Footer";
import { RouteStatus } from "@ugg/shared/components/common/RouteStatus";
import { GET_USER_SETTINGS } from "@ugg/shared/api/graphql/queries";
import { AppRoutes, RouterPaths } from "@ugg/shared/routes/app-routes";
import { RiotIdRedirect } from "@ugg/shared/components/SummonerProfiles/RiotIdRedirect";
import { useUserPremiumState } from "@ugg/shared/api/requests/accounts/user-state";
import PremiumFAQ from "components/Accounts/PremiumFAQ";
import OpenSearchRedirect from "components/OpenSearchRedirect";
import { Error404 } from "@ugg/shared/components/common/Error404";
import AdWhitelist from "components/Ads/AdWhitelist";
import Login from "components/Accounts/Login";
import Signup from "components/Accounts/Signup";
import VerificationContainer from "components/Pages/VerificationPage/views/VerificationContainer";
import NotFoundUserSummonerProfile from "components/Accounts/NotFoundUserSummonerProfile/views/NotFoundUserSummonerProfile";
import { useBanner } from "@ugitgud/legos/hooks/useBanner";
import { REMATCH_BANNER_CTA } from "@ugitgud/legos/components/banner/RematchBanner";

// Loadable Components
const LandingPageContainer = loadable(() => import("components/Pages/LandingPage/LandingPageContainer"));
const DesktopAppSplashPage = loadable(() => import("components/DesktopAppSplashPage/DesktopAppSplashPage"));
const SupportUsPage = loadable(() => import("components/Pages/SupportUsPage"));
const LeaderboardsPage = loadable(() => import("components/Leaderboards/LeaderboardsPage"), {
  resolveComponent: (components) => components.LeaderboardsPage,
});
const TierListPage = loadable(() => import("components/TierListPage"), {
  resolveComponent: (components) => components.TierListPage,
});
const ChampionsRoutes = loadable(() => import("components/ChampionsRoutes"), {
  resolveComponent: (components) => components.ChampionsRoutes,
});
const ChampionsPage = loadable(() => import("components/Champions/ChampionsPage"), {
  resolveComponent: (components) => components.ChampionsPage,
});
const SummonerProfile = loadable(() => import("components/SummonerProfiles/SummonerProfile"), {
  resolveComponent: (components) => components.SummonerProfile,
});
const Settings = loadable(() => import("components/Accounts/Settings/Settings"));
const RecoverAccount = loadable(() => import("components/Accounts/RecoverAccount/RecoverAccount"));
const PasswordReset = loadable(() => import("components/Accounts/PasswordReset"));
const FAQ = loadable(() => import("components/Pages/FAQ"));
const PrivacyPolicy = loadable(() => import("components/PrivacyPolicy"));
const PrivacyShield = loadable(() => import("components/PrivacyShield"));
const TermsOfService = loadable(() => import("components/TermsOfService"));
const PremiumCheckout = loadable(() => import("components/Premium/views/PremiumCheckout"));
const LiveGameSplashContainer = loadable(() => import("components/SummonerProfiles/LiveGame/LiveGameSplash"), {
  resolveComponent: (components) => components.LiveGameSplashContainer,
});
const EmailVerificationPage = loadable(() => import("components/Pages/EmailVerificationPage"));
const ItemsPage = loadable(() => import("components/Items/ItemsPage"), {
  resolveComponent: (components) => components.ItemsPage,
});
const ItemsDirectory = loadable(() => import("components/Items/ItemsDirectory"), {
  resolveComponent: (components) => components.ItemsDirectory,
});
const MultiSearchMain = loadable(() => import("components/MultiSearch/MultiSearchMain"));

const WatchList = loadable(() => import("components/WatchLive/WatchListPage"), {
  resolveComponent: (components) => components.WatchListPage,
});

const MainContent = () => {
  const [authToken] = useGlobal("authToken");
  const location = useLocation();

  const { data: userSettings } = useQuery(GET_USER_SETTINGS, { fetchPolicy: "network-only", skip: !authToken });
  const [loggingIn] = useGlobal("loggingIn");
  const { data, loading } = useUserPremiumState();
  const isAdFree = loading || loggingIn || data?.isPremium;

  useEffect(() => {
    if (userSettings) {
      if (!window?.ramp) {
        window.ramp = window.ramp || { que: [] };
        window.ramp.que.push(() => {
          window.ramp.setUserProfile({
            eid: window.btoa(userSettings?.getSettings?.email),
            format: "EUP1",
          });
        });
      } else {
        window.ramp.up = {
          format: "EUP1",
          eid: window.btoa(userSettings?.getSettings?.email),
        };
      }
    }
  }, [userSettings]);

  useEffect(() => {
    if (!isAdFree && window.admiral) {
      console.log("Resume Admiral Scripts");
      window.admiral("targeting", "ready");
    }
  }, [isAdFree]);

  const { open } = useBanner(REMATCH_BANNER_CTA);

  const disclaimerText =
    "U.GG Stats isn't endorsed by Riot Games and doesn't reflect the views or opinions of Riot Games or anyone officially involved in producing or managing League of Legends.";

  // min-height: calc(100vh - var(--masthead-height) - var(--game-nav-height));
  const contentStyle = open
    ? `calc(100vh - var(--masthead-height) - var(--game-nav-height) - var(--banner-height))`
    : "calc(100vh - var(--masthead-height) - var(--game-nav-height))";

  return (
    <div id="main-content" className="collapsed">
      {/* DO NOT REMOVE 'main-content' id or 'collapsed' class. REQUIRED FOR ADS!!!! */}
      <div id="content-wrapper">
        {/* DO NOT REMOVE 'content-wrapper' id. REQUIRED FOR ADS!!!! */}
        <div id="content" style={{ minHeight: contentStyle }}>
          {/* DO NOT REMOVE 'content' id. REQUIRED FOR ADS!!!! */}
          <Switch>
            <Route exact path={AppRoutes.HOME} component={LandingPageContainer} />
            <Route exact path={AppRoutes.UGG_APP_SPLASH} component={DesktopAppSplashPage} />
            <Route exact path={AppRoutes.SUPPORT_US} component={SupportUsPage} />
            <Route exact path={AppRoutes.LIVE_GAMES} component={LiveGameSplashContainer} />
            <Route path={AppRoutes.VERIFY} component={EmailVerificationPage} />
            <Route path={AppRoutes.NOT_FOUND_PROFILE} component={NotFoundUserSummonerProfile} />
            <Route path={AppRoutes.PROFILE_ROOT} component={SummonerProfile} />
            <Route
              path={AppRoutes.PROFILE_ROOT_OLD}
              component={RiotIdRedirect}
              /* Must come after AppRoutes.PROFILE_ROOT */
            />
            <Route path={RouterPaths.TIER_LIST} component={TierListPage} />
            <Route path={AppRoutes.CHAMPION_HOME} exact component={ChampionsPage} />
            <Route path={AppRoutes.CHAMPION_LEADERBOARD} component={ChampionsPage} />
            <Route path={AppRoutes.CHAMPION_ROOT} component={ChampionsRoutes} />
            <Route path={AppRoutes.ITEM_WIKI} component={ItemsPage} />
            <Route path={AppRoutes.ITEMS_DIRECTORY} component={ItemsDirectory} />
            {/* Leaderboards */}
            <Route path={AppRoutes.LEADERBOARD} component={LeaderboardsPage} />
            <Redirect path={AppRoutes.OLD_LEADERBOARD} to={AppRoutes.LEADERBOARD} />

            <Route path={AppRoutes.TOS} render={(props) => <TermsOfService {...props} />} />
            <Route path={AppRoutes.ADBLOCK_WHITELIST} render={(props) => <AdWhitelist {...props} />} />
            <Route path={AppRoutes.PRIVACY_POLICY} render={(props) => <PrivacyPolicy {...props} />} />
            {/* <Route path={AppRoutes.PRIVACY_SHIELD} render={(props) => <PrivacyShield {...props} />} /> */}
            <Route path={AppRoutes.OPENSEARCH} render={(props) => <OpenSearchRedirect {...props} />} />
            <Route path={AppRoutes.MULTISEARCH} render={(props) => <MultiSearchMain {...props} />} />
            <Route path={AppRoutes.SETTINGS} component={Settings} />
            <Route path={AppRoutes.RECOVER_ACCOUNT} component={RecoverAccount} />
            <Route path={AppRoutes.RESET_PASSWORD} component={PasswordReset} />
            <Route path={AppRoutes.FAQ} render={(props) => <FAQ {...props} />} />
            <Route path={AppRoutes.LOGIN} render={(props) => <Login {...props} />} />
            <Route path={AppRoutes.SIGNUP} render={(props) => <Signup {...props} />} />
            <Route path={AppRoutes.PREMIUM} component={PremiumCheckout} />
            <Route path={AppRoutes.PREMIUM_FAQ} render={(props) => <PremiumFAQ {...props} />} />
            <Route path={AppRoutes.VERIFICATION} render={(props) => <VerificationContainer />} />
            <Route path={AppRoutes.WatchList} render={(props) => <WatchList {...props} />} />
            <RouteStatus statusCode={404} path="*">
              <Error404 />
            </RouteStatus>
          </Switch>
        </div>
        <div className="content-side-padding">
          <Footer disclaimerText={disclaimerText} path={location.pathname} />
        </div>
      </div>
    </div>
  );
};

export default MainContent;
